import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom-v6'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Empty,
  EmptyHeader,
  EmptyTitle,
  Skeleton,
  Switch,
} from '@clouway/ui'
import { Pencil, Plus, Trash2 } from 'lucide-react'

import { useDeleteOAuthClient, useOAuthClients, useSetOAuthClientActive } from '../hooks'

export function OAuthClientsList() {
  const { t } = useTranslation('feature')
  const { t: tCommon } = useTranslation('common')
  const { data: clients, isLoading } = useOAuthClients()
  const { mutate: remove } = useDeleteOAuthClient()
  const { mutate: setActive } = useSetOAuthClientActive()

  if (isLoading) {
    return <Skeleton className="mx-auto h-64 w-full max-w-3xl" />
  }

  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-3">
      <div className="flex justify-end">
        <Button asChild>
          <Link to="/apis/new">
            <Plus className="size-4" />
            {t('oauthClient.list.add')}
          </Link>
        </Button>
      </div>

      {clients && clients.length === 0 && (
        <Empty>
          <EmptyHeader>
            <EmptyTitle>{t('oauthClient.list.empty')}</EmptyTitle>
          </EmptyHeader>
        </Empty>
      )}

      {clients?.map((client) => (
        <Card key={client.id}>
          <CardHeader>
            <div className="flex items-start justify-between gap-2">
              <div>
                <CardTitle className="text-sm">{client.description || client.id}</CardTitle>
                <p className="text-muted-foreground font-mono text-xs">{client.id}</p>
              </div>
              <div className="flex items-center gap-1">
                <Switch
                  checked={client.active}
                  onCheckedChange={(active) => setActive({ id: client.id, active })}
                  aria-label={t('oauthClient.list.active')}
                />
                <Button asChild size="icon" variant="ghost" aria-label="edit">
                  <Link to={`/apis/${client.id}/edit`}>
                    <Pencil className="size-4" />
                  </Link>
                </Button>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button size="icon" variant="ghost" aria-label="delete">
                      <Trash2 className="size-4" />
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>{t('oauthClient.list.delete')}</AlertDialogTitle>
                      <AlertDialogDescription>
                        {t('oauthClient.list.deleteDescription', { client: client.description || client.id })}
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>{tCommon('ui.button.cancel')}</AlertDialogCancel>
                      <AlertDialogAction onClick={() => remove(client.id)}>
                        {tCommon('ui.button.confirm')}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            </div>
          </CardHeader>
          {client.redirectURLs.length > 0 && (
            <CardContent className="flex flex-col gap-1">
              {client.redirectURLs.map((url) => (
                <span key={url} className="text-muted-foreground font-mono text-xs">
                  {url}
                </span>
              ))}
            </CardContent>
          )}
        </Card>
      ))}
    </div>
  )
}
